import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { 
  Users, Calendar, CheckCircle2, Phone, AlertTriangle, RefreshCw, 
  Eye, ChevronLeft, ChevronRight, X, Clock, UserCheck, Ban,
  PhoneOutgoing, PhoneIncoming, PhoneMissed, MessageSquare, TrendingUp, BarChart3,
  Timer, Award, Target, GitCompare, HelpCircle, User
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./components/ui/tooltip";

// Composants extraits
import { UrgentR1Banner, UrgentAppelQualiteBanner, AlertesConfirmationRDV, AppelsMissedBanner, FatimaAlertsWidget, AlertesCarousel } from './components/telepro';
import SanIABanner from './components/telepro/SanIABanner';
import SanIAChat from './components/SanIAChat';
import { CalendrierButton } from './components/calendrier/CalendrierButton';
import { MonEspaceButton } from './components/MonEspaceButton';
import BanniereLeadsLive from './components/BanniereLeadsLive';
import MesRappelsWidget from './components/MesRappelsWidget';
import CommunicationInterneWidget from './components/SuperBatard/widgets/CommunicationInterneWidget';
import MaTodoListWidget from './components/SuperBatard/widgets/MaTodoListWidget';
import VisaIAWidget from './components/SuperBatard/widgets/VisaIAWidget';
import TaskNotificationBadge from './components/TaskNotificationBadge';
import PipelineGroupedPills from './components/PipelineGroupedPills';
import getApiUrl from './utils/getApiUrl';

// Composant helper pour les tooltips d'aide
const HelpTooltip = ({ children, content }) => (
  <Tooltip delayDuration={100}>
    <TooltipTrigger asChild>
      <div className="flex items-center gap-1 cursor-help">
        {children}
        <HelpCircle className="w-3 h-3 text-slate-500 hover:text-slate-300" />
      </div>
    </TooltipTrigger>
    <TooltipContent className="max-w-xs bg-slate-800 text-white border border-slate-600 px-3 py-2">
      <p className="text-sm">{content}</p>
    </TooltipContent>
  </Tooltip>
);

const API = `${getApiUrl()}/api`;

// Colonnes spécifiques pour Télépro
const TELEPRO_COLUMNS = {
  TELEPRO: 14,
  RDV_CONFIRME: 19,
  DATE_RDV: 20,
  DATE_R1: 21,
  DATE_APPEL_QUALITE: 22,
  RESULTAT_APPEL_QUALITE: 23,
  ETATS_GENERAUX: 10,
  CLIENT_NOM: 1,
  PRENOM: 2,
  DPT: 3,
  LOGEMENT: 5,
  CATEGORIE: 6
};

function StatCard({ icon: Icon, label, value, subValue, color, onClick, active, testId }) {
  return (
    <div 
      data-testid={testId}
      onClick={onClick}
      className={`bg-slate-800 border rounded-xl p-4 cursor-pointer transition-all hover:scale-105 ${
        active ? 'border-blue-500 ring-2 ring-blue-500/30' : 'border-slate-700 hover:border-slate-600'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-slate-400 text-xs uppercase tracking-wider">{label}</span>
        <div className={`w-8 h-8 ${color} rounded-lg flex items-center justify-center`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      {subValue && <p className="text-xs text-slate-500 mt-1">{subValue}</p>}
    </div>
  );
}

function TeleprosFilter({ telepros, selected, onSelect }) {
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
      <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
        <UserCheck className="w-4 h-4 text-blue-400" />
        Par Télépro
      </h3>
      <div className="space-y-1 max-h-[280px] overflow-y-auto">
        {telepros.map(t => (
          <button
            key={t.name}
            onClick={() => onSelect(selected === t.name ? null : t.name)}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
              selected === t.name 
                ? 'bg-blue-600 text-white' 
                : 'text-slate-300 hover:bg-slate-700'
            }`}
          >
            <span>{t.name}</span>
            <span className={`font-bold ${selected === t.name ? 'text-white' : 'text-blue-400'}`}>
              {t.count}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ResultsFilter({ results, selected, onSelect }) {
  const getColor = (name) => {
    if (name.includes('OK') || name.includes('CONFIRMÉ')) return 'text-green-400';
    if (name.includes('PAS FAIT') || name.includes('REPOSITIONNER')) return 'text-orange-400';
    if (name.includes('PAS SIGNÉ') || name.includes('BLOQUÉ')) return 'text-red-400';
    return 'text-slate-400';
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
      <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
        <CheckCircle2 className="w-4 h-4 text-green-400" />
        Résultats Appels
      </h3>
      <div className="space-y-1 max-h-[280px] overflow-y-auto">
        {results.map(r => (
          <button
            key={r.name}
            onClick={() => onSelect(selected === r.name ? null : r.name)}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs transition-colors ${
              selected === r.name 
                ? 'bg-slate-600 text-white' 
                : 'text-slate-300 hover:bg-slate-700'
            }`}
          >
            <span className={`truncate ${getColor(r.name)}`}>{r.name}</span>
            <span className="font-bold text-white ml-2">{r.count}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function QuickActions({ stats, onAction }) {
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
      <h3 className="text-sm font-bold text-white mb-3">Actions Rapides</h3>
      <div className="grid grid-cols-2 gap-2">
        <button 
          onClick={() => onAction('relancer')}
          className="bg-slate-700 hover:bg-slate-600 rounded-lg p-3 text-left transition-colors"
        >
          <div className="flex items-center gap-2 mb-1">
            <Phone className="w-4 h-4 text-orange-400" />
            <span className="text-xs text-slate-400">Clients à relancer</span>
          </div>
          <p className="text-lg font-bold text-white">{stats.aRelancer} clients</p>
        </button>
        <button 
          onClick={() => onAction('qualite')}
          className="bg-slate-700 hover:bg-slate-600 rounded-lg p-3 text-left transition-colors"
        >
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 className="w-4 h-4 text-purple-400" />
            <span className="text-xs text-slate-400">Appels Qualité</span>
          </div>
          <p className="text-lg font-bold text-white">{stats.qualiteAFaire} à faire</p>
        </button>
        <button 
          onClick={() => onAction('bloques')}
          className="bg-slate-700 hover:bg-slate-600 rounded-lg p-3 text-left transition-colors"
        >
          <div className="flex items-center gap-2 mb-1">
            <Ban className="w-4 h-4 text-red-400" />
            <span className="text-xs text-slate-400">Clients Bloqués</span>
          </div>
          <p className="text-lg font-bold text-white">{stats.bloques} clients</p>
        </button>
        <button 
          onClick={() => onAction('tous')}
          className="bg-slate-700 hover:bg-slate-600 rounded-lg p-3 text-left transition-colors"
        >
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-blue-400" />
            <span className="text-xs text-slate-400">Tous les clients</span>
          </div>
          <p className="text-lg font-bold text-white">{stats.total} clients</p>
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// COLLABORATOR DETAIL MODAL
// ═══════════════════════════════════════════════════════════════
function CollaboratorDetailModal({ collaborator, startDate, endDate, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDetails();
  }, [collaborator, startDate, endDate]);

  const loadDetails = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const res = await axios.get(`${API}/ringover/collaborators/${encodeURIComponent(collaborator.name)}/details?${params}`);
      setData(res.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  // Filter only weekdays (Lundi-Vendredi)
  const weekdayData = data?.by_day?.filter(d => d.day_num >= 2 && d.day_num <= 6) || [];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-700 bg-slate-800">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{collaborator.name}</h2>
              <p className="text-slate-400 text-sm">Statistiques détaillées • {startDate} → {endDate}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto max-h-[calc(90vh-100px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-purple-400" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Distribution par jour (Lundi-Vendredi) */}
              <div className="bg-slate-800 rounded-xl p-4">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-400" />
                  Distribution par jour (Lun-Ven)
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={weekdayData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="day" stroke="#9CA3AF" fontSize={12} />
                      <YAxis stroke="#9CA3AF" fontSize={12} />
                      <RechartsTooltip 
                        contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                        labelStyle={{ color: '#F9FAFB' }}
                      />
                      <Legend />
                      <Bar dataKey="outbound" name="Émis" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="inbound" name="Reçus" fill="#06B6D4" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="answered" name="Décrochés" fill="#10B981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Distribution par heure */}
              <div className="bg-slate-800 rounded-xl p-4">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-purple-400" />
                  Distribution par heure (pic d'activité)
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data?.by_hour || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="label" stroke="#9CA3AF" fontSize={10} />
                      <YAxis stroke="#9CA3AF" fontSize={12} />
                      <RechartsTooltip 
                        contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                        labelStyle={{ color: '#F9FAFB' }}
                      />
                      <Bar dataKey="total" name="Appels" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {/* Pic horaire */}
                {data?.by_hour?.length > 0 && (
                  <div className="mt-3 flex items-center gap-4 text-sm">
                    <span className="text-slate-400">Pic d'activité:</span>
                    <span className="text-purple-400 font-bold">
                      {data.by_hour.reduce((max, h) => h.total > max.total ? h : max, data.by_hour[0])?.label}
                    </span>
                    <span className="text-slate-500">
                      ({data.by_hour.reduce((max, h) => h.total > max.total ? h : max, data.by_hour[0])?.total} appels)
                    </span>
                  </div>
                )}
              </div>

              {/* Timeline journalière */}
              <div className="bg-slate-800 rounded-xl p-4">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                  Évolution journalière
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data?.daily_breakdown || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="date_label" stroke="#9CA3AF" fontSize={10} />
                      <YAxis stroke="#9CA3AF" fontSize={12} />
                      <RechartsTooltip 
                        contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                        labelStyle={{ color: '#F9FAFB' }}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="outbound" name="Émis" stroke="#3B82F6" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="answered" name="Décrochés" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="answer_rate" name="Taux (%)" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Récapitulatif hebdomadaire */}
              <div className="bg-slate-800 rounded-xl p-4">
                <h3 className="text-lg font-bold text-white mb-4">Récapitulatif par semaine</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-700">
                        <th className="pb-2">Semaine</th>
                        <th className="pb-2 text-center">Total</th>
                        <th className="pb-2 text-center">Émis</th>
                        <th className="pb-2 text-center">Décrochés</th>
                        <th className="pb-2 text-center">Taux</th>
                        <th className="pb-2 text-center">Heures</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {data?.weekly_summary?.map((week, i) => (
                        <tr key={i} className="text-sm">
                          <td className="py-2 text-white font-medium">{week.week} <span className="text-slate-500">({week.start_date})</span></td>
                          <td className="py-2 text-center text-slate-300">{week.total}</td>
                          <td className="py-2 text-center text-blue-400">{week.outbound}</td>
                          <td className="py-2 text-center text-green-400">{week.answered}</td>
                          <td className="py-2 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                              week.answer_rate >= 70 ? 'bg-green-600/20 text-green-400' : 
                              week.answer_rate >= 50 ? 'bg-yellow-600/20 text-yellow-400' : 'bg-red-600/20 text-red-400'
                            }`}>
                              {week.answer_rate}%
                            </span>
                          </td>
                          <td className="py-2 text-center text-purple-400">{week.duration_hours}h</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// COLLABORATOR COMPARISON MODAL
// ═══════════════════════════════════════════════════════════════
function CollaboratorComparisonModal({ collaborators, selected, startDate, endDate, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState('outbound');

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];

  useEffect(() => {
    if (selected.length > 0) {
      loadComparison();
    }
  }, [selected, startDate, endDate]);

  const loadComparison = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      selected.forEach(name => params.append('telepros', name));
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const res = await axios.get(`${API}/ringover/collaborators/compare?${params}`);
      setData(res.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const metrics = [
    { key: 'outbound', label: 'Appels Émis', suffix: '_outbound' },
    { key: 'answered', label: 'Décrochés', suffix: '_answered' },
    { key: 'rate', label: 'Taux (%)', suffix: '_rate' },
    { key: 'duration', label: 'Durée (min)', suffix: '_duration' },
    { key: 'total', label: 'Total Appels', suffix: '_total' }
  ];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-700 bg-slate-800">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-600 rounded-lg flex items-center justify-center">
              <GitCompare className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Comparaison Collaborateurs</h2>
              <p className="text-slate-400 text-sm">{selected.length} sélectionnés • {startDate} → {endDate}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Metric selector */}
        <div className="p-4 bg-slate-800/50 border-b border-slate-700">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-slate-400">Métrique:</span>
            {metrics.map(m => (
              <button
                key={m.key}
                onClick={() => setMetric(m.key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  metric === m.key 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          
          {/* Legend */}
          <div className="flex items-center gap-4 mt-3 flex-wrap">
            {selected.map((name, i) => (
              <div key={name} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                <span className="text-sm text-slate-300">{name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Chart */}
        <div className="p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-400" />
            </div>
          ) : (
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data?.comparison || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="date_label" stroke="#9CA3AF" fontSize={11} />
                  <YAxis stroke="#9CA3AF" fontSize={12} />
                  <RechartsTooltip 
                    contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                    labelStyle={{ color: '#F9FAFB' }}
                  />
                  <Legend />
                  {selected.map((name, i) => (
                    <Line 
                      key={name}
                      type="monotone" 
                      dataKey={`${name}${metrics.find(m => m.key === metric)?.suffix || '_total'}`} 
                      name={name}
                      stroke={COLORS[i % COLORS.length]} 
                      strokeWidth={2} 
                      dot={{ r: 4 }}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Les bannières UrgentR1Banner et UrgentAppelQualiteBanner sont maintenant importées depuis ./components/telepro


// ═══════════════════════════════════════════════════════════════
// COLLABORATOR EFFICIENCY COMPONENT (UPDATED)
// ═══════════════════════════════════════════════════════════════
function CollaboratorEfficiency({ defaultExpanded = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodFilter, setPeriodFilter] = useState('week'); // week, month, lastMonth, custom
  const [startDate, setStartDate] = useState(() => {
    // Par défaut: semaine en cours (lundi de cette semaine)
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Lundi
    d.setDate(diff);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [selectedCollaborator, setSelectedCollaborator] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);

  const updatePeriodFilter = (filter) => {
    setPeriodFilter(filter);
    const now = new Date();
    let start, end;
    
    switch (filter) {
      case 'week':
        // Semaine en cours (lundi à aujourd'hui)
        const day = now.getDay();
        const diff = now.getDate() - day + (day === 0 ? -6 : 1);
        start = new Date(now);
        start.setDate(diff);
        end = now;
        break;
      case 'month':
        // Ce mois
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = now;
        break;
      case 'lastMonth':
        // Mois dernier
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'custom':
        return; // Garder les dates actuelles
      default:
        return;
    }
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  useEffect(() => {
    if (expanded) {
      loadStats();
    }
  }, [startDate, endDate, expanded]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const res = await axios.get(`${API}/ringover/collaborators/stats?${params}`);
      setData(res.data);
    } catch (err) {
      console.error("Error loading collaborator stats:", err);
    }
    setLoading(false);
  };

  const formatDuration = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const toggleCompareSelection = (name) => {
    setSelectedForCompare(prev => 
      prev.includes(name) 
        ? prev.filter(n => n !== name)
        : [...prev, name]
    );
  };

  return (
    <TooltipProvider>
    <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => setExpanded(!expanded)}
          >
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center">
              <Phone className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-white flex items-center gap-2">
                Efficacité Télépros - Appels téléphoniques
                {expanded && (
                  <span className="text-xs bg-purple-600/30 text-purple-300 px-2 py-0.5 rounded">
                    {data?.collaborators?.length || 0} télépros
                  </span>
                )}
              </h2>
              <p className="text-xs text-slate-400">
                {expanded ? 'Cliquez sur un collaborateur pour voir les détails' : 'Cliquez pour ouvrir'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {expanded && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCompareMode(!compareMode);
                    if (compareMode) setSelectedForCompare([]);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                    compareMode ? 'bg-green-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  <GitCompare className="w-4 h-4" />
                  {compareMode ? 'Annuler' : 'Comparer'}
                </button>
                {compareMode && selectedForCompare.length >= 2 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowCompareModal(true);
                    }}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-medium text-white"
                  >
                    Voir comparaison ({selectedForCompare.length})
                  </button>
                )}
              </>
            )}
            <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </div>
        </div>
      </div>

      {expanded && (
        <>
          {/* Period Filter + Date Range */}
          <div className="p-4 bg-slate-900/50 border-b border-slate-700">
            <div className="flex items-center gap-4 flex-wrap">
              {/* Period Quick Filters */}
              <div className="flex items-center gap-1 bg-slate-700 rounded-lg p-1">
                <button
                  onClick={() => updatePeriodFilter('week')}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    periodFilter === 'week' ? 'bg-purple-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Cette semaine
                </button>
                <button
                  onClick={() => updatePeriodFilter('month')}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    periodFilter === 'month' ? 'bg-purple-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Ce mois
                </button>
                <button
                  onClick={() => updatePeriodFilter('lastMonth')}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    periodFilter === 'lastMonth' ? 'bg-purple-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Mois dernier
                </button>
                <button
                  onClick={() => setPeriodFilter('custom')}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    periodFilter === 'custom' ? 'bg-purple-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Personnalisé
                </button>
              </div>
              
              {/* Date inputs */}
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setPeriodFilter('custom');
                  }}
                  className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-sm"
                />
                <span className="text-slate-500">→</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setPeriodFilter('custom');
                  }}
                  className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-sm"
                />
              </div>
              <button
                onClick={loadStats}
                className="p-2 hover:bg-slate-700 rounded-lg ml-auto"
              >
                <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Team Totals */}
          {data?.team_totals && (
            <div className="p-4 border-b border-slate-700">
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <div className="bg-slate-800 rounded-lg p-3 cursor-help">
                      <div className="flex items-center gap-2 mb-1">
                        <PhoneOutgoing className="w-4 h-4 text-blue-400" />
                        <span className="text-xs text-slate-400">Émis</span>
                        <HelpCircle className="w-3 h-3 text-slate-500 ml-auto" />
                      </div>
                      <p className="text-xl font-bold text-white">{data.team_totals.outbound_total}</p>
                      <p className="text-xs text-green-400">{data.team_totals.outbound_rate}% décrochés</p>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs bg-slate-800 text-white border border-slate-600">
                    <p>Appels sortants passés par l'équipe (tentatives d'appels vers les prospects/clients)</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <div className="bg-slate-800 rounded-lg p-3 cursor-help">
                      <div className="flex items-center gap-2 mb-1">
                        <PhoneIncoming className="w-4 h-4 text-cyan-400" />
                        <span className="text-xs text-slate-400">Reçus</span>
                        <HelpCircle className="w-3 h-3 text-slate-500 ml-auto" />
                      </div>
                      <p className="text-xl font-bold text-white">{data.team_totals.inbound_total}</p>
                      <p className="text-xs text-green-400">{data.team_totals.inbound_rate}% répondus</p>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs bg-slate-800 text-white border border-slate-600">
                    <p>Appels entrants reçus par l'équipe (prospects/clients qui rappellent)</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <div className="bg-slate-800 rounded-lg p-3 cursor-help">
                      <div className="flex items-center gap-2 mb-1">
                        <PhoneMissed className="w-4 h-4 text-red-400" />
                        <span className="text-xs text-slate-400">NRP</span>
                        <HelpCircle className="w-3 h-3 text-slate-500 ml-auto" />
                      </div>
                      <p className="text-xl font-bold text-red-400">{data.team_totals.outbound_nrp || 0}</p>
                      <p className="text-xs text-slate-400">non répondus</p>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs bg-slate-800 text-white border border-slate-600">
                    <p>Non Répondu au Poste : prospects/clients marqués comme injoignables après plusieurs tentatives</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <div className="bg-slate-800 rounded-lg p-3 cursor-help">
                      <div className="flex items-center gap-2 mb-1">
                        <MessageSquare className="w-4 h-4 text-yellow-400" />
                        <span className="text-xs text-slate-400">Répondeurs</span>
                        <HelpCircle className="w-3 h-3 text-slate-500 ml-auto" />
                      </div>
                      <p className="text-xl font-bold text-yellow-400">{data.team_totals.outbound_voicemail || 0}</p>
                      <p className="text-xs text-slate-400">messageries</p>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs bg-slate-800 text-white border border-slate-600">
                    <p>Appels tombés sur répondeur : sonnerie longue (≥15s) sans réponse ou communication très courte (&lt;10s)</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <div className="bg-slate-800 rounded-lg p-3 cursor-help">
                      <div className="flex items-center gap-2 mb-1">
                        <Timer className="w-4 h-4 text-purple-400" />
                        <span className="text-xs text-slate-400">Temps total</span>
                        <HelpCircle className="w-3 h-3 text-slate-500 ml-auto" />
                      </div>
                      <p className="text-xl font-bold text-white">{data.team_totals.duration_formatted}</p>
                      <p className="text-xs text-slate-400">en communication</p>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs bg-slate-800 text-white border border-slate-600">
                    <p>Temps total passé en communication téléphonique (appels entrants + sortants décrochés)</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <div className="bg-slate-800 rounded-lg p-3 cursor-help">
                      <div className="flex items-center gap-2 mb-1">
                        <MessageSquare className="w-4 h-4 text-green-400" />
                        <span className="text-xs text-slate-400">SMS</span>
                        <HelpCircle className="w-3 h-3 text-slate-500 ml-auto" />
                      </div>
                      <p className="text-xl font-bold text-white">{data.team_totals.sms_sent + data.team_totals.sms_received}</p>
                      <p className="text-xs text-slate-400">📤 {data.team_totals.sms_sent} 📥 {data.team_totals.sms_received}</p>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs bg-slate-800 text-white border border-slate-600">
                    <p>SMS envoyés (📤) et reçus (📥) par l'équipe</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          )}

          {/* Collaborators Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="w-6 h-6 animate-spin text-purple-400 mx-auto" />
                <p className="text-slate-400 mt-2">Chargement des statistiques...</p>
              </div>
            ) : data?.collaborators?.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Aucune donnée pour cette période</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-900/80">
                  <tr>
                    {compareMode && <th className="px-2 py-3"></th>}
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Collaborateur</th>
                    <th className="text-center px-3 py-3 text-xs uppercase tracking-wider text-slate-400">
                      <HelpTooltip content="Appels sortants passés par le télépro (tentatives d'appels vers les prospects/clients)">
                        <div className="flex items-center gap-1">
                          <PhoneOutgoing className="w-3 h-3" />
                          Émis
                        </div>
                      </HelpTooltip>
                    </th>
                    <th className="text-center px-3 py-3 text-xs uppercase tracking-wider text-slate-400">
                      <HelpTooltip content="Appels émis auxquels le prospect/client a répondu (communication réelle, hors répondeur)">
                        <span>Décrochés</span>
                      </HelpTooltip>
                    </th>
                    <th className="text-center px-3 py-3 text-xs uppercase tracking-wider text-slate-400">
                      <HelpTooltip content="Pourcentage d'appels émis qui ont été décrochés (Décrochés / Émis × 100)">
                        <span>Taux</span>
                      </HelpTooltip>
                    </th>
                    <th className="text-center px-3 py-3 text-xs uppercase tracking-wider text-slate-400">
                      <HelpTooltip content="Appels entrants reçus par le télépro (prospects/clients qui rappellent)">
                        <div className="flex items-center gap-1">
                          <PhoneIncoming className="w-3 h-3" />
                          Reçus
                        </div>
                      </HelpTooltip>
                    </th>
                    <th className="text-center px-3 py-3 text-xs uppercase tracking-wider text-slate-400">
                      <HelpTooltip content="Appels entrants non répondus par le télépro (le prospect a appelé mais le télépro n'a pas décroché)">
                        <span>Manqués</span>
                      </HelpTooltip>
                    </th>
                    <th className="text-center px-3 py-3 text-xs uppercase tracking-wider text-slate-400">
                      <HelpTooltip content="Non Répondu au Poste : prospects/clients marqués comme injoignables après plusieurs tentatives d'appels">
                        <div className="flex items-center gap-1">
                          <PhoneMissed className="w-3 h-3 text-red-400" />
                          NRP
                        </div>
                      </HelpTooltip>
                    </th>
                    <th className="text-center px-3 py-3 text-xs uppercase tracking-wider text-slate-400">
                      <HelpTooltip content="Appels tombés sur répondeur/messagerie vocale : sonnerie longue (≥15s) sans réponse humaine ou communication très courte (<10s)">
                        <div className="flex items-center gap-1">
                          <MessageSquare className="w-3 h-3 text-yellow-400" />
                          Répondeur
                        </div>
                      </HelpTooltip>
                    </th>
                    <th className="text-center px-3 py-3 text-xs uppercase tracking-wider text-slate-400">
                      <HelpTooltip content="Temps total passé en communication téléphonique (appels entrants + sortants décrochés)">
                        <div className="flex items-center gap-1">
                          <Timer className="w-3 h-3" />
                          Temps
                        </div>
                      </HelpTooltip>
                    </th>
                    <th className="text-center px-3 py-3 text-xs uppercase tracking-wider text-slate-400">
                      <HelpTooltip content="Durée moyenne par appel décroché (Temps total / Nombre d'appels décrochés)">
                        <span>Moy/appel</span>
                      </HelpTooltip>
                    </th>
                    <th className="text-center px-3 py-3 text-xs uppercase tracking-wider text-slate-400">
                      <HelpTooltip content="SMS envoyés (📤) et reçus (📥) par le télépro">
                        <span>SMS</span>
                      </HelpTooltip>
                    </th>
                    <th className="text-center px-3 py-3 text-xs uppercase tracking-wider text-slate-400">
                      <HelpTooltip content="Score d'efficacité global basé sur : volume d'appels, taux de décrochés, temps de communication et activité SMS">
                        <div className="flex items-center gap-1">
                          <Target className="w-3 h-3" />
                          Score
                        </div>
                      </HelpTooltip>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {data?.collaborators?.map((collab, idx) => (
                    <tr 
                      key={collab.name} 
                      className={`hover:bg-slate-700/30 cursor-pointer transition-colors ${
                        selectedForCompare.includes(collab.name) ? 'bg-blue-600/20' : ''
                      }`}
                      onClick={() => compareMode ? toggleCompareSelection(collab.name) : setSelectedCollaborator(collab)}
                    >
                      {compareMode && (
                        <td className="px-2 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedForCompare.includes(collab.name)}
                            onChange={() => {}}
                            className="w-4 h-4 rounded bg-slate-700 border-slate-600"
                          />
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                            idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-orange-600' : 'bg-slate-600'
                          }`}>
                            {idx + 1}
                          </div>
                          <span className="font-medium text-white">{collab.name}</span>
                          {!compareMode && <Eye className="w-4 h-4 text-slate-500 ml-auto" />}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="font-bold text-blue-400">{collab.outbound.total}</span>
                      </td>
                      <td className="px-3 py-3 text-center text-green-400">{collab.outbound.answered}</td>
                      <td className="px-3 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                          collab.outbound.rate >= 70 ? 'bg-green-600/20 text-green-400' :
                          collab.outbound.rate >= 50 ? 'bg-yellow-600/20 text-yellow-400' :
                          'bg-red-600/20 text-red-400'
                        }`}>
                          {collab.outbound.rate}%
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center text-cyan-400">{collab.inbound.total}</td>
                      <td className="px-3 py-3 text-center text-orange-400">{collab.inbound.missed}</td>
                      <td className="px-3 py-3 text-center">
                        {collab.outbound.nrp > 0 ? (
                          <span className="text-red-400 font-medium">{collab.outbound.nrp}</span>
                        ) : (
                          <span className="text-slate-500">-</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center">
                        {collab.outbound.voicemail > 0 ? (
                          <span className="text-yellow-400 font-medium">{collab.outbound.voicemail}</span>
                        ) : (
                          <span className="text-slate-500">-</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center text-purple-400 font-medium">
                        {collab.duration.formatted}
                      </td>
                      <td className="px-3 py-3 text-center text-slate-400 text-sm">
                        {formatDuration(collab.duration.avg_per_call_sec)}
                      </td>
                      <td className="px-3 py-3 text-center text-slate-400 text-sm">
                        {collab.sms.sent + collab.sms.received > 0 ? (
                          <span>📤{collab.sms.sent} 📥{collab.sms.received}</span>
                        ) : '-'}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={`px-2 py-1 rounded font-bold text-sm ${
                          collab.efficiency_score >= 60 ? 'bg-green-600/20 text-green-400' :
                          collab.efficiency_score >= 40 ? 'bg-yellow-600/20 text-yellow-400' :
                          'bg-red-600/20 text-red-400'
                        }`}>
                          {collab.efficiency_score}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* Detail Modal */}
      {selectedCollaborator && (
        <CollaboratorDetailModal
          collaborator={selectedCollaborator}
          startDate={startDate}
          endDate={endDate}
          onClose={() => setSelectedCollaborator(null)}
        />
      )}

      {/* Comparison Modal */}
      {showCompareModal && (
        <CollaboratorComparisonModal
          collaborators={data?.collaborators || []}
          selected={selectedForCompare}
          startDate={startDate}
          endDate={endDate}
          onClose={() => setShowCompareModal(false)}
        />
      )}
    </div>
    </TooltipProvider>
  );
}


// ═══════════════════════════════════════════════════════════════
// EFFICACITÉ TÉLÉPROS - R1 COMPONENT
// ═══════════════════════════════════════════════════════════════
function R1Efficiency({ allClients, telepros, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [periodFilter, setPeriodFilter] = useState('week'); // week, month, lastMonth, custom
  const [startDate, setStartDate] = useState(() => {
    // Par défaut: semaine en cours (lundi de cette semaine)
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [viewMode, setViewMode] = useState('stats'); // 'stats', 'table', 'chart'
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [compareMode, setCompareMode] = useState(false);

  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  const updatePeriodFilter = (filter) => {
    setPeriodFilter(filter);
    const now = new Date();
    let start, end;
    
    switch (filter) {
      case 'week':
        const day = now.getDay();
        const diff = now.getDate() - day + (day === 0 ? -6 : 1);
        start = new Date(now);
        start.setDate(diff);
        end = now;
        break;
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = now;
        break;
      case 'lastMonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'custom':
        return;
      default:
        return;
    }
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  // Helper pour générer une clé de date sans problème de timezone
  const getDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Filtrer les RDV pris par période et jours ouvrés uniquement
  const filteredR1 = useMemo(() => {
    if (!allClients) return [];
    
    return allClients.filter(client => {
      const datePriseRdv = client.datePriseRdv;  // DATE DE PRISE DE RDV
      if (!datePriseRdv) return false;
      
      const parts = datePriseRdv.split('/');
      if (parts.length !== 3) return false;
      
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      const date = new Date(year, month - 1, day);
      const dayOfWeek = date.getDay();
      
      // Exclure samedi (6) et dimanche (0)
      if (dayOfWeek === 0 || dayOfWeek === 6) return false;
      
      // Filtrer par date
      if (startDate) {
        const start = new Date(startDate + 'T00:00:00');
        if (date < start) return false;
      }
      if (endDate) {
        const end = new Date(endDate + 'T23:59:59');
        if (date > end) return false;
      }
      
      return true;
    });
  }, [allClients, startDate, endDate]);

  // Stats par télépro avec décomposition par source
  const statsByTelepro = useMemo(() => {
    const stats = {};
    filteredR1.forEach(client => {
      const telepro = client.telepro || 'Non assigné';
      const source = client.source || 'Autre';
      
      if (!stats[telepro]) {
        stats[telepro] = { total: 0, confirmed: 0, sources: {} };
      }
      stats[telepro].total++;
      if (client.rdvConfirme?.toLowerCase() === 'oui') {
        stats[telepro].confirmed++;
      }
      
      // Compter par source
      if (!stats[telepro].sources[source]) {
        stats[telepro].sources[source] = 0;
      }
      stats[telepro].sources[source]++;
    });
    return Object.entries(stats)
      .map(([name, data]) => ({
        name,
        total: data.total,
        confirmed: data.confirmed,
        rate: data.total > 0 ? Math.round(data.confirmed / data.total * 100) : 0,
        sources: data.sources
      }))
      .sort((a, b) => b.total - a.total);
  }, [filteredR1]);

  // Stats par jour pour le graphique
  const statsByDay = useMemo(() => {
    const statsMap = {};
    
    filteredR1.forEach(client => {
      const datePriseRdv = client.datePriseRdv;  // DATE DE PRISE DE RDV
      if (!datePriseRdv) return;
      
      const parts = datePriseRdv.split('/');
      if (parts.length !== 3) return;
      
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      const date = new Date(year, month - 1, day);
      const dateKey = getDateKey(date);
      const dayName = dayNames[date.getDay()];
      const label = `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')} (${dayName})`;
      
      if (!statsMap[dateKey]) {
        statsMap[dateKey] = { date: dateKey, label, total: 0, confirmed: 0, byTelepro: {} };
      }
      statsMap[dateKey].total++;
      if (client.rdvConfirme?.toLowerCase() === 'oui') {
        statsMap[dateKey].confirmed++;
      }
      
      const telepro = client.telepro || 'Non assigné';
      if (!statsMap[dateKey].byTelepro[telepro]) {
        statsMap[dateKey].byTelepro[telepro] = 0;
      }
      statsMap[dateKey].byTelepro[telepro]++;
    });
    
    // Générer tous les jours ouvrés
    const allDays = [];
    if (startDate && endDate) {
      const start = new Date(startDate + 'T12:00:00');
      const end = new Date(endDate + 'T12:00:00');
      
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dayOfWeek = d.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          const dateKey = getDateKey(d);
          const dayName = dayNames[dayOfWeek];
          const label = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} (${dayName})`;
          
          if (statsMap[dateKey]) {
            allDays.push(statsMap[dateKey]);
          } else {
            allDays.push({ date: dateKey, label, total: 0, confirmed: 0, byTelepro: {} });
          }
        }
      }
    }
    
    return allDays.length > 0 ? allDays : Object.values(statsMap).sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredR1, startDate, endDate]);

  // Données de comparaison
  const comparisonData = useMemo(() => {
    if (selectedForCompare.length === 0) return statsByDay;
    
    return statsByDay.map(day => {
      const data = { date: day.date, label: day.label };
      selectedForCompare.forEach(telepro => {
        data[telepro] = day.byTelepro[telepro] || 0;
      });
      return data;
    });
  }, [statsByDay, selectedForCompare]);

  const toggleCompareSelection = (name) => {
    setSelectedForCompare(prev => 
      prev.includes(name) 
        ? prev.filter(n => n !== name)
        : prev.length < 5 ? [...prev, name] : prev
    );
  };

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  const totalRdvPris = filteredR1.length;
  const totalConfirmed = statsByTelepro.reduce((a, b) => a + b.confirmed, 0);
  const confirmationRate = totalRdvPris > 0 ? Math.round(totalConfirmed / totalRdvPris * 100) : 0;

  return (
    <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => setExpanded(!expanded)}
          >
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-600 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-white flex items-center gap-2">
                Efficacité Télépros - RDV Pris
                {expanded && (
                  <span className="text-xs bg-green-600/30 text-green-300 px-2 py-0.5 rounded">
                    {totalRdvPris} RDV pris
                  </span>
                )}
              </h2>
              <p className="text-xs text-slate-400">
                {expanded ? 'RDV pris par télépro (DATE DE PRISE DE RDV)' : 'Cliquez pour ouvrir'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {expanded && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCompareMode(!compareMode);
                    if (compareMode) setSelectedForCompare([]);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                    compareMode ? 'bg-green-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  <GitCompare className="w-4 h-4" />
                  {compareMode ? 'Annuler' : 'Comparer'}
                </button>
              </>
            )}
            <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </div>
        </div>
      </div>

      {expanded && (
        <>
          {/* Period Filter */}
          <div className="p-4 bg-slate-900/50 border-b border-slate-700">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-1 bg-slate-700 rounded-lg p-1">
                <button
                  onClick={() => updatePeriodFilter('week')}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    periodFilter === 'week' ? 'bg-green-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Cette semaine
                </button>
                <button
                  onClick={() => updatePeriodFilter('month')}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    periodFilter === 'month' ? 'bg-green-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Ce mois
                </button>
                <button
                  onClick={() => updatePeriodFilter('lastMonth')}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    periodFilter === 'lastMonth' ? 'bg-green-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Mois dernier
                </button>
                <button
                  onClick={() => setPeriodFilter('custom')}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    periodFilter === 'custom' ? 'bg-green-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Personnalisé
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setPeriodFilter('custom');
                  }}
                  className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-sm"
                />
                <span className="text-slate-500">→</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setPeriodFilter('custom');
                  }}
                  className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-sm"
                />
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="p-4 border-b border-slate-700">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-slate-800 rounded-lg p-3">
                <p className="text-xs text-slate-400">Total RDV Pris</p>
                <p className="text-2xl font-bold text-white">{totalRdvPris}</p>
              </div>
              <div className="bg-slate-800 rounded-lg p-3">
                <p className="text-xs text-slate-400">Confirmés</p>
                <p className="text-2xl font-bold text-green-400">{totalConfirmed}</p>
              </div>
              <div className="bg-slate-800 rounded-lg p-3">
                <p className="text-xs text-slate-400">Taux confirmation</p>
                <p className="text-2xl font-bold text-blue-400">{confirmationRate}%</p>
              </div>
              <div className="bg-slate-800 rounded-lg p-3">
                <p className="text-xs text-slate-400">Moy./jour</p>
                <p className="text-2xl font-bold text-purple-400">
                  {statsByDay.length > 0 ? Math.round(totalRdvPris / statsByDay.length * 10) / 10 : 0}
                </p>
              </div>
            </div>
          </div>

          {/* View Mode Toggle */}
          <div className="p-4 border-b border-slate-700">
            <div className="flex items-center gap-2">
              <div className="flex bg-slate-700 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('stats')}
                  className={`px-3 py-1 rounded text-sm ${viewMode === 'stats' ? 'bg-green-600 text-white' : 'text-slate-400'}`}
                >
                  Par Télépro
                </button>
                <button
                  onClick={() => setViewMode('chart')}
                  className={`px-3 py-1 rounded text-sm ${viewMode === 'chart' ? 'bg-green-600 text-white' : 'text-slate-400'}`}
                >
                  Graphique
                </button>
                {compareMode && selectedForCompare.length >= 2 && (
                  <button
                    onClick={() => setViewMode('compare')}
                    className={`px-3 py-1 rounded text-sm ${viewMode === 'compare' ? 'bg-green-600 text-white' : 'text-slate-400'}`}
                  >
                    Comparer ({selectedForCompare.length})
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 max-h-[500px] overflow-auto">
            {viewMode === 'stats' && (
              <div className="space-y-2">
                {statsByTelepro.length === 0 ? (
                  <p className="text-slate-400 text-center py-8">Aucun RDV pris pour cette période</p>
                ) : (
                  statsByTelepro.map((telepro, idx) => (
                    <div
                      key={telepro.name}
                      onClick={(e) => {
                        // Ne pas déclencher si on clique sur la checkbox
                        if (e.target.type !== 'checkbox' && compareMode) {
                          toggleCompareSelection(telepro.name);
                        }
                      }}
                      className={`p-3 rounded-lg transition-all ${compareMode ? 'cursor-pointer' : ''} ${
                        compareMode && selectedForCompare.includes(telepro.name)
                          ? 'bg-green-600/20 border border-green-500'
                          : 'bg-slate-800 hover:bg-slate-700 border border-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {compareMode && (
                            <input
                              type="checkbox"
                              checked={selectedForCompare.includes(telepro.name)}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleCompareSelection(telepro.name);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 rounded cursor-pointer accent-green-500"
                            />
                          )}
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                          />
                          <span className="text-white font-medium">{telepro.name}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <span className="text-lg font-bold text-white">{telepro.total}</span>
                            <span className="text-xs text-slate-400 ml-1">RDV</span>
                            {/* Sources breakdown */}
                            {telepro.sources && Object.keys(telepro.sources).length > 0 && (
                              <span className="text-xs text-slate-500 ml-1">
                                ({Object.entries(telepro.sources).map(([src, count], i) => (
                                  <span key={src}>
                                    {i > 0 && ', '}{count} {src}
                                  </span>
                                ))})
                              </span>
                            )}
                          </div>
                          <div className="text-right">
                            <span className="text-lg font-bold text-green-400">{telepro.confirmed}</span>
                            <span className="text-xs text-slate-400 ml-1">confirmés</span>
                          </div>
                          <div className="w-16 text-right">
                            <span className={`text-sm font-bold ${telepro.rate >= 70 ? 'text-green-400' : telepro.rate >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                              {telepro.rate}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {viewMode === 'chart' && (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={statsByDay}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="label" stroke="#9CA3AF" fontSize={10} angle={-45} textAnchor="end" height={60} />
                    <YAxis stroke="#9CA3AF" fontSize={12} />
                    <RechartsTooltip 
                      contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155', borderRadius: '8px' }}
                      labelStyle={{ color: '#E2E8F0' }}
                    />
                    <Legend />
                    <Bar dataKey="confirmed" name="Confirmés" fill="#10B981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="total" name="Total RDV Pris" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {viewMode === 'compare' && selectedForCompare.length >= 2 && (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={comparisonData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="label" stroke="#9CA3AF" fontSize={10} angle={-45} textAnchor="end" height={60} />
                    <YAxis stroke="#9CA3AF" fontSize={12} />
                    <RechartsTooltip 
                      contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155', borderRadius: '8px' }}
                      labelStyle={{ color: '#E2E8F0' }}
                    />
                    <Legend />
                    {selectedForCompare.map((name, idx) => (
                      <Line
                        key={name}
                        type="monotone"
                        dataKey={name}
                        name={name}
                        stroke={COLORS[idx % COLORS.length]}
                        strokeWidth={2}
                        dot={{ fill: COLORS[idx % COLORS.length], strokeWidth: 2 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// EFFICACITÉ TÉLÉPROS - APPELS VS R1 PRIS
// ═══════════════════════════════════════════════════════════════
function AppelsVsR1Efficiency({ allClients, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [periodFilter, setPeriodFilter] = useState('week');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [callsData, setCallsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 'table', 'chart', 'daily'

  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  const updatePeriodFilter = (filter) => {
    setPeriodFilter(filter);
    const now = new Date();
    let start, end;
    
    switch (filter) {
      case 'week':
        const day = now.getDay();
        const diff = now.getDate() - day + (day === 0 ? -6 : 1);
        start = new Date(now);
        start.setDate(diff);
        end = now;
        break;
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = now;
        break;
      case 'lastMonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'custom':
        return;
      default:
        return;
    }
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  // Charger les stats Ringover
  useEffect(() => {
    if (expanded) {
      loadCallStats();
    }
  }, [startDate, endDate, expanded]);

  const loadCallStats = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const res = await axios.get(`${API}/ringover/collaborators/stats?${params}`);
      setCallsData(res.data);
    } catch (err) {
      console.error("Error loading call stats:", err);
    }
    setLoading(false);
  };

  // Helper pour générer une clé de date
  const getDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Filtrer les RDV pris par période (jours ouvrés uniquement)
  const filteredR1 = useMemo(() => {
    if (!allClients) return [];
    
    return allClients.filter(client => {
      const datePriseRdv = client.datePriseRdv;
      if (!datePriseRdv) return false;
      
      const parts = datePriseRdv.split('/');
      if (parts.length !== 3) return false;
      
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      const date = new Date(year, month - 1, day);
      const dayOfWeek = date.getDay();
      
      // Exclure samedi (6) et dimanche (0)
      if (dayOfWeek === 0 || dayOfWeek === 6) return false;
      
      if (startDate) {
        const start = new Date(startDate + 'T00:00:00');
        if (date < start) return false;
      }
      if (endDate) {
        const end = new Date(endDate + 'T23:59:59');
        if (date > end) return false;
      }
      
      return true;
    });
  }, [allClients, startDate, endDate]);

  // Stats R1 par télépro
  const r1ByTelepro = useMemo(() => {
    const stats = {};
    filteredR1.forEach(client => {
      const telepro = client.telepro || 'Non assigné';
      if (!stats[telepro]) {
        stats[telepro] = { total: 0, confirmed: 0 };
      }
      stats[telepro].total++;
      if (client.rdvConfirme?.toLowerCase() === 'oui') {
        stats[telepro].confirmed++;
      }
    });
    return stats;
  }, [filteredR1]);

  // Combiner les données appels et R1 par télépro
  const combinedStats = useMemo(() => {
    if (!callsData?.collaborators) return [];
    
    return callsData.collaborators.map(collab => {
      const r1Stats = r1ByTelepro[collab.name] || { total: 0, confirmed: 0 };
      const totalCalls = collab.outbound.total + collab.inbound.total;
      const answeredCalls = collab.outbound.answered + collab.inbound.answered;
      const r1Pris = r1Stats.total;
      
      // Ratio = appels décrochés pour 1 R1 pris
      const ratio = r1Pris > 0 ? Math.round(answeredCalls / r1Pris * 10) / 10 : 0;
      // Taux de conversion = R1 pris / appels décrochés * 100
      const conversionRate = answeredCalls > 0 ? Math.round(r1Pris / answeredCalls * 1000) / 10 : 0;
      
      return {
        name: collab.name,
        totalCalls,
        outboundCalls: collab.outbound.total,
        answeredCalls,
        outboundAnswered: collab.outbound.answered,
        answerRate: collab.outbound.rate,
        r1Pris,
        r1Confirmed: r1Stats.confirmed,
        ratio,
        conversionRate,
        duration: collab.duration.formatted,
        durationSec: collab.duration.total_seconds
      };
    }).sort((a, b) => b.r1Pris - a.r1Pris);
  }, [callsData, r1ByTelepro]);

  // Stats par jour pour le graphique
  const dailyStats = useMemo(() => {
    if (!allClients || !startDate || !endDate) return [];
    
    const statsMap = {};
    
    // Initialiser tous les jours ouvrés
    const start = new Date(startDate + 'T12:00:00');
    const end = new Date(endDate + 'T12:00:00');
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dayOfWeek = d.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        const dateKey = getDateKey(d);
        const dayName = dayNames[dayOfWeek];
        const label = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} (${dayName})`;
        statsMap[dateKey] = { date: dateKey, label, r1Pris: 0 };
      }
    }
    
    // Ajouter les R1 pris
    filteredR1.forEach(client => {
      const datePriseRdv = client.datePriseRdv;
      if (!datePriseRdv) return;
      
      const parts = datePriseRdv.split('/');
      if (parts.length !== 3) return;
      
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      const date = new Date(year, month - 1, day);
      const dateKey = getDateKey(date);
      
      if (statsMap[dateKey]) {
        statsMap[dateKey].r1Pris++;
      }
    });
    
    return Object.values(statsMap).sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredR1, startDate, endDate]);

  // Totaux
  const totalCalls = combinedStats.reduce((sum, s) => sum + s.totalCalls, 0);
  const totalAnswered = combinedStats.reduce((sum, s) => sum + s.answeredCalls, 0);
  const totalR1 = combinedStats.reduce((sum, s) => sum + s.r1Pris, 0);
  const totalR1Confirmed = combinedStats.reduce((sum, s) => sum + s.r1Confirmed, 0);
  const globalRatio = totalR1 > 0 ? Math.round(totalAnswered / totalR1 * 10) / 10 : 0;
  const globalConversion = totalAnswered > 0 ? Math.round(totalR1 / totalAnswered * 1000) / 10 : 0;

  return (
    <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => setExpanded(!expanded)}
          >
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-white flex items-center gap-2">
                Efficacité Télépros - Appels vs R1 pris
                {expanded && (
                  <span className="text-xs bg-orange-600/30 text-orange-300 px-2 py-0.5 rounded">
                    {globalConversion}% conversion
                  </span>
                )}
              </h2>
              <p className="text-xs text-slate-400">
                {expanded ? 'Comparatif appels téléphoniques et RDV R1 obtenus' : 'Cliquez pour ouvrir'}
              </p>
            </div>
          </div>
          <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </div>

      {expanded && (
        <>
          {/* Period Filter */}
          <div className="p-4 bg-slate-900/50 border-b border-slate-700">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-1 bg-slate-700 rounded-lg p-1">
                <button
                  onClick={() => updatePeriodFilter('week')}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    periodFilter === 'week' ? 'bg-orange-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Cette semaine
                </button>
                <button
                  onClick={() => updatePeriodFilter('month')}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    periodFilter === 'month' ? 'bg-orange-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Ce mois
                </button>
                <button
                  onClick={() => updatePeriodFilter('lastMonth')}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    periodFilter === 'lastMonth' ? 'bg-orange-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Mois dernier
                </button>
                <button
                  onClick={() => setPeriodFilter('custom')}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    periodFilter === 'custom' ? 'bg-orange-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Personnalisé
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setPeriodFilter('custom');
                  }}
                  className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-sm"
                />
                <span className="text-slate-500">→</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setPeriodFilter('custom');
                  }}
                  className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-sm"
                />
              </div>

              <button
                onClick={loadCallStats}
                className="p-2 hover:bg-slate-700 rounded-lg ml-auto"
              >
                <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="p-4 border-b border-slate-700">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <div className="bg-slate-800 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <PhoneOutgoing className="w-4 h-4 text-blue-400" />
                  <span className="text-xs text-slate-400">Appels émis</span>
                </div>
                <p className="text-xl font-bold text-white">{combinedStats.reduce((s, c) => s + c.outboundCalls, 0)}</p>
              </div>
              <div className="bg-slate-800 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Phone className="w-4 h-4 text-green-400" />
                  <span className="text-xs text-slate-400">Décrochés</span>
                </div>
                <p className="text-xl font-bold text-green-400">{totalAnswered}</p>
              </div>
              <div className="bg-slate-800 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-cyan-400" />
                  <span className="text-xs text-slate-400">R1 Pris</span>
                </div>
                <p className="text-xl font-bold text-cyan-400">{totalR1}</p>
              </div>
              <div className="bg-slate-800 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span className="text-xs text-slate-400">R1 Confirmés</span>
                </div>
                <p className="text-xl font-bold text-emerald-400">{totalR1Confirmed}</p>
              </div>
              <div className="bg-gradient-to-br from-orange-600/20 to-red-600/20 rounded-lg p-3 border border-orange-500/30">
                <div className="flex items-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-orange-400" />
                  <span className="text-xs text-orange-300">Conversion</span>
                </div>
                <p className="text-xl font-bold text-orange-400">{globalConversion}%</p>
                <p className="text-xs text-slate-400">R1/appels décrochés</p>
              </div>
              <div className="bg-slate-800 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <BarChart3 className="w-4 h-4 text-purple-400" />
                  <span className="text-xs text-slate-400">Ratio</span>
                </div>
                <p className="text-xl font-bold text-purple-400">{globalRatio}</p>
                <p className="text-xs text-slate-400">appels/R1</p>
              </div>
            </div>
          </div>

          {/* View Mode Toggle */}
          <div className="p-4 border-b border-slate-700">
            <div className="flex items-center gap-2">
              <div className="flex bg-slate-700 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-1 rounded text-sm ${viewMode === 'table' ? 'bg-orange-600 text-white' : 'text-slate-400'}`}
                >
                  Par Télépro
                </button>
                <button
                  onClick={() => setViewMode('chart')}
                  className={`px-3 py-1 rounded text-sm ${viewMode === 'chart' ? 'bg-orange-600 text-white' : 'text-slate-400'}`}
                >
                  Graphique comparatif
                </button>
                <button
                  onClick={() => setViewMode('daily')}
                  className={`px-3 py-1 rounded text-sm ${viewMode === 'daily' ? 'bg-orange-600 text-white' : 'text-slate-400'}`}
                >
                  Évolution journalière
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-orange-400" />
              </div>
            ) : viewMode === 'table' ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-900/80">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Télépro</th>
                      <th className="text-center px-3 py-3 text-xs uppercase tracking-wider text-slate-400">
                        <div className="flex items-center justify-center gap-1">
                          <PhoneOutgoing className="w-3 h-3" />
                          Émis
                        </div>
                      </th>
                      <th className="text-center px-3 py-3 text-xs uppercase tracking-wider text-slate-400">Décrochés</th>
                      <th className="text-center px-3 py-3 text-xs uppercase tracking-wider text-slate-400">Taux</th>
                      <th className="text-center px-3 py-3 text-xs uppercase tracking-wider text-slate-400">
                        <div className="flex items-center justify-center gap-1">
                          <Calendar className="w-3 h-3" />
                          R1 Pris
                        </div>
                      </th>
                      <th className="text-center px-3 py-3 text-xs uppercase tracking-wider text-slate-400">Confirmés</th>
                      <th className="text-center px-3 py-3 text-xs uppercase tracking-wider text-slate-400 bg-orange-600/10">
                        <div className="flex items-center justify-center gap-1">
                          <Target className="w-3 h-3 text-orange-400" />
                          Conversion
                        </div>
                      </th>
                      <th className="text-center px-3 py-3 text-xs uppercase tracking-wider text-slate-400">
                        Ratio appels/R1
                      </th>
                      <th className="text-center px-3 py-3 text-xs uppercase tracking-wider text-slate-400">Temps</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {combinedStats.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="px-4 py-8 text-center text-slate-400">
                          Aucune donnée pour cette période
                        </td>
                      </tr>
                    ) : (
                      combinedStats.map((stat, idx) => (
                        <tr key={stat.name} className="hover:bg-slate-700/30">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                                idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-orange-600' : 'bg-slate-600'
                              }`}>
                                {idx + 1}
                              </div>
                              <span className="font-medium text-white">{stat.name}</span>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className="font-bold text-blue-400">{stat.outboundCalls}</span>
                          </td>
                          <td className="px-3 py-3 text-center text-green-400">{stat.outboundAnswered}</td>
                          <td className="px-3 py-3 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                              stat.answerRate >= 70 ? 'bg-green-600/20 text-green-400' :
                              stat.answerRate >= 50 ? 'bg-yellow-600/20 text-yellow-400' :
                              'bg-red-600/20 text-red-400'
                            }`}>
                              {stat.answerRate}%
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className="font-bold text-cyan-400">{stat.r1Pris}</span>
                          </td>
                          <td className="px-3 py-3 text-center text-emerald-400">{stat.r1Confirmed}</td>
                          <td className="px-3 py-3 text-center bg-orange-600/5">
                            <span className={`px-2 py-1 rounded text-sm font-bold ${
                              stat.conversionRate >= 10 ? 'bg-green-600/20 text-green-400' :
                              stat.conversionRate >= 5 ? 'bg-yellow-600/20 text-yellow-400' :
                              stat.conversionRate > 0 ? 'bg-orange-600/20 text-orange-400' :
                              'text-slate-500'
                            }`}>
                              {stat.conversionRate}%
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            {stat.ratio > 0 ? (
                              <span className="text-purple-400 font-medium">{stat.ratio}</span>
                            ) : (
                              <span className="text-slate-500">-</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-center text-slate-400">{stat.duration}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : viewMode === 'chart' ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={combinedStats.slice(0, 10)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis type="number" stroke="#9CA3AF" fontSize={12} />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      stroke="#9CA3AF" 
                      fontSize={11} 
                      width={100}
                      tickFormatter={(value) => value.length > 12 ? value.substring(0, 12) + '...' : value}
                    />
                    <RechartsTooltip 
                      contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                      labelStyle={{ color: '#F9FAFB' }}
                    />
                    <Legend />
                    <Bar dataKey="outboundAnswered" name="Appels décrochés" fill="#10B981" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="r1Pris" name="R1 Pris" fill="#06B6D4" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailyStats}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="label" stroke="#9CA3AF" fontSize={10} angle={-45} textAnchor="end" height={60} />
                    <YAxis stroke="#9CA3AF" fontSize={12} />
                    <RechartsTooltip 
                      contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                      labelStyle={{ color: '#F9FAFB' }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="r1Pris" 
                      name="R1 Pris" 
                      stroke="#06B6D4" 
                      strokeWidth={2} 
                      dot={{ fill: '#06B6D4', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// RINGOVER STATS COMPONENT
// ═══════════════════════════════════════════════════════════════
function RingoverStatsCard({ stats, loading, selectedTelepro }) {
  if (loading) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 className="w-4 h-4 text-blue-400" />
          <h3 className="text-sm font-bold text-white">Stats Ringover</h3>
        </div>
        <div className="flex items-center justify-center py-6">
          <RefreshCw className="w-5 h-5 animate-spin text-blue-400" />
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const callStats = stats.calls || {};
  const smsStats = stats.sms || {};

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="w-4 h-4 text-blue-400" />
        <h3 className="text-sm font-bold text-white">Stats Ringover</h3>
        {selectedTelepro && (
          <span className="text-xs bg-blue-600/30 text-blue-300 px-2 py-0.5 rounded">
            {selectedTelepro}
          </span>
        )}
      </div>
      
      {/* Appels */}
      <div className="space-y-3">
        <div className="bg-slate-900/50 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <Phone className="w-4 h-4 text-green-400" />
            <span className="text-xs text-slate-400 uppercase">Appels</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-lg font-bold text-white">{callStats.total || 0}</p>
              <p className="text-xs text-slate-500">Total</p>
            </div>
            <div>
              <p className="text-lg font-bold text-green-400">{callStats.answer_rate || 0}%</p>
              <p className="text-xs text-slate-500">Taux décroché</p>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-2 text-xs">
            <span className="flex items-center gap-1">
              <PhoneOutgoing className="w-3 h-3 text-blue-400" />
              <span className="text-slate-400">{callStats.outbound || 0} sortants</span>
            </span>
            <span className="flex items-center gap-1">
              <PhoneIncoming className="w-3 h-3 text-cyan-400" />
              <span className="text-slate-400">{callStats.inbound || 0} entrants</span>
            </span>
          </div>
          {callStats.nrp > 0 && (
            <div className="flex items-center gap-1 mt-2">
              <PhoneMissed className="w-3 h-3 text-red-400" />
              <span className="text-xs text-red-400 font-medium">{callStats.nrp} NRP</span>
            </div>
          )}
        </div>

        {/* SMS */}
        <div className="bg-slate-900/50 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare className="w-4 h-4 text-blue-400" />
            <span className="text-xs text-slate-400 uppercase">SMS</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-lg font-bold text-white">{smsStats.total || 0}</p>
              <p className="text-xs text-slate-500">Total</p>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="text-slate-400">📤 {smsStats.sent || 0}</span>
              <span className="text-slate-400">📥 {smsStats.received || 0}</span>
            </div>
          </div>
        </div>

        {/* Durée moyenne */}
        {callStats.avg_duration_sec > 0 && (
          <div className="flex items-center justify-between text-xs bg-slate-900/30 px-3 py-2 rounded">
            <span className="text-slate-400">Durée moy. appel</span>
            <span className="text-white font-medium">
              {Math.floor(callStats.avg_duration_sec / 60)}m {Math.round(callStats.avg_duration_sec % 60)}s
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function DateRangePicker({ startDate, endDate, onStartChange, onEndChange }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-2">
        <label className="text-xs text-slate-400">Du:</label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => onStartChange(e.target.value)}
          className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-slate-400">Au:</label>
        <input
          type="date"
          value={endDate}
          onChange={(e) => onEndChange(e.target.value)}
          className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </div>
  );
}

function RdvPrisModal({ isOpen, onClose, telepros, allClients }) {
  const [selectedTelepro, setSelectedTelepro] = useState('');
  const [periodType, setPeriodType] = useState('2weeks'); // '2weeks', 'month', 'custom'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table', 'chart', 'compare'
  const [selectedForCompare, setSelectedForCompare] = useState([]);

  // Noms des jours
  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  // Helper pour formater une date avec le jour
  const formatDateWithDay = (dateStr) => {
    if (!dateStr) return '-';
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const date = new Date(parts[2], parts[1] - 1, parts[0]);
      const dayName = dayNames[date.getDay()];
      return `${dateStr} (${dayName})`;
    }
    return dateStr;
  };

  // Initialiser les dates selon la période
  useEffect(() => {
    if (isOpen) {
      updatePeriod(periodType);
    }
  }, [isOpen, periodType]);

  const updatePeriod = (type) => {
    const now = new Date();
    let start, end;
    
    switch (type) {
      case '2weeks':
        start = new Date(now);
        start.setDate(start.getDate() - 14);
        end = now;
        break;
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'custom':
        return; // Garder les dates actuelles
      default:
        start = new Date(now);
        start.setDate(start.getDate() - 14);
        end = now;
    }
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const filteredRdv = useMemo(() => {
    if (!allClients) return [];
    
    return allClients.filter(client => {
      const datePriseRdv = client.datePriseRdv;
      if (!datePriseRdv) return false;

      // Filtrer par télépro
      if (selectedTelepro && client.telepro !== selectedTelepro) return false;

      // Filtrer par date
      if (startDate || endDate) {
        const parts = datePriseRdv.split('/');
        if (parts.length === 3) {
          const rdvDate = new Date(parts[2], parts[1] - 1, parts[0]);
          if (startDate && rdvDate < new Date(startDate)) return false;
          if (endDate && rdvDate > new Date(endDate)) return false;
        }
      }

      return true;
    });
  }, [allClients, selectedTelepro, startDate, endDate]);

  // Filtrer les RDV pour n'inclure que les jours ouvrés (lun-ven)
  const filteredRdvBusinessDays = useMemo(() => {
    return filteredRdv.filter(client => {
      const datePriseRdv = client.datePriseRdv;
      if (!datePriseRdv) return false;
      
      const parts = datePriseRdv.split('/');
      if (parts.length !== 3) return false;
      
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      const date = new Date(year, month - 1, day);
      const dayOfWeek = date.getDay();
      
      // Exclure samedi (6) et dimanche (0)
      return dayOfWeek !== 0 && dayOfWeek !== 6;
    });
  }, [filteredRdv]);

  // Calculer les stats par télépro (jours ouvrés uniquement)
  const statsByTelepro = useMemo(() => {
    const stats = {};
    filteredRdvBusinessDays.forEach(client => {
      const telepro = client.telepro || 'Non assigné';
      if (!stats[telepro]) {
        stats[telepro] = { total: 0, confirmed: 0 };
      }
      stats[telepro].total++;
      if (client.rdvConfirme?.toLowerCase() === 'oui') {
        stats[telepro].confirmed++;
      }
    });
    return Object.entries(stats)
      .map(([name, data]) => ({
        name,
        total: data.total,
        confirmed: data.confirmed,
        rate: data.total > 0 ? Math.round(data.confirmed / data.total * 100) : 0
      }))
      .sort((a, b) => b.total - a.total);
  }, [filteredRdvBusinessDays]);

  // Helper pour générer une clé de date sans problème de timezone
  const getDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calculer les stats par jour (inclure tous les jours ouvrés)
  const statsByDay = useMemo(() => {
    // D'abord, collecter les données existantes (jours ouvrés uniquement)
    const statsMap = {};
    filteredRdvBusinessDays.forEach(client => {
      const datePriseRdv = client.datePriseRdv;
      if (!datePriseRdv) return;
      
      const parts = datePriseRdv.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        const date = new Date(year, month - 1, day);
        // Utiliser getDateKey au lieu de toISOString pour éviter les problèmes de timezone
        const dateKey = getDateKey(date);
        const dayName = dayNames[date.getDay()];
        const label = `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')} (${dayName})`;
        
        if (!statsMap[dateKey]) {
          statsMap[dateKey] = { date: dateKey, label, total: 0, confirmed: 0, byTelepro: {} };
        }
        statsMap[dateKey].total++;
        if (client.rdvConfirme?.toLowerCase() === 'oui') {
          statsMap[dateKey].confirmed++;
        }
        
        // Stats par télépro pour comparaison
        const telepro = client.telepro || 'Non assigné';
        if (!statsMap[dateKey].byTelepro[telepro]) {
          statsMap[dateKey].byTelepro[telepro] = 0;
        }
        statsMap[dateKey].byTelepro[telepro]++;
      }
    });
    
    // Générer tous les jours ouvrés (lundi-vendredi) entre startDate et endDate
    const allDays = [];
    if (startDate && endDate) {
      const start = new Date(startDate + 'T12:00:00'); // Midi pour éviter les problèmes de timezone
      const end = new Date(endDate + 'T12:00:00');
      
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dayOfWeek = d.getDay();
        // Exclure samedi (6) et dimanche (0)
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          const dateKey = getDateKey(d);
          const dayName = dayNames[dayOfWeek];
          const label = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} (${dayName})`;
          
          if (statsMap[dateKey]) {
            allDays.push(statsMap[dateKey]);
          } else {
            allDays.push({
              date: dateKey,
              label,
              total: 0,
              confirmed: 0,
              byTelepro: {}
            });
          }
        }
      }
    }
    
    return allDays.length > 0 ? allDays : Object.values(statsMap).sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredRdvBusinessDays, startDate, endDate]);

  // Données pour graphique de comparaison
  const comparisonData = useMemo(() => {
    if (selectedForCompare.length === 0) return statsByDay;
    
    return statsByDay.map(day => {
      const data = { date: day.date, label: day.label };
      selectedForCompare.forEach(telepro => {
        data[telepro] = day.byTelepro[telepro] || 0;
      });
      return data;
    });
  }, [statsByDay, selectedForCompare]);

  const toggleCompareSelection = (name) => {
    setSelectedForCompare(prev => 
      prev.includes(name) 
        ? prev.filter(n => n !== name)
        : prev.length < 5 ? [...prev, name] : prev
    );
  };

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-white">RDV Pris - Analyse</h2>
              <p className="text-xs text-slate-400">{filteredRdvBusinessDays.length} rendez-vous (jours ouvrés) • {statsByTelepro.reduce((a, b) => a + b.confirmed, 0)} confirmés</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* View Mode Toggle */}
            <div className="flex bg-slate-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1 rounded text-sm ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'text-slate-400'}`}
              >
                Tableau
              </button>
              <button
                onClick={() => setViewMode('chart')}
                className={`px-3 py-1 rounded text-sm ${viewMode === 'chart' ? 'bg-blue-600 text-white' : 'text-slate-400'}`}
              >
                Graphique
              </button>
              <button
                onClick={() => setViewMode('compare')}
                className={`px-3 py-1 rounded text-sm ${viewMode === 'compare' ? 'bg-blue-600 text-white' : 'text-slate-400'}`}
              >
                Comparer
              </button>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-slate-700 bg-slate-900/50">
          <div className="flex flex-wrap items-center gap-4">
            {/* Période prédéfinie */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">Période:</span>
              <div className="flex bg-slate-700 rounded-lg p-0.5">
                <button
                  onClick={() => setPeriodType('2weeks')}
                  className={`px-3 py-1 rounded text-xs font-medium ${periodType === '2weeks' ? 'bg-purple-600 text-white' : 'text-slate-400 hover:text-white'}`}
                >
                  2 semaines
                </button>
                <button
                  onClick={() => setPeriodType('month')}
                  className={`px-3 py-1 rounded text-xs font-medium ${periodType === 'month' ? 'bg-purple-600 text-white' : 'text-slate-400 hover:text-white'}`}
                >
                  Ce mois
                </button>
                <button
                  onClick={() => setPeriodType('custom')}
                  className={`px-3 py-1 rounded text-xs font-medium ${periodType === 'custom' ? 'bg-purple-600 text-white' : 'text-slate-400 hover:text-white'}`}
                >
                  Personnalisé
                </button>
              </div>
            </div>

            {/* Dates (visible only in custom mode or always for fine-tuning) */}
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPeriodType('custom'); }}
                className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white text-sm"
              />
              <span className="text-slate-500">→</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPeriodType('custom'); }}
                className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white text-sm"
              />
            </div>

            {/* Télépro filter */}
            <select
              value={selectedTelepro}
              onChange={(e) => setSelectedTelepro(e.target.value)}
              className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-sm"
            >
              <option value="">Tous les télépros</option>
              {telepros.map(t => (
                <option key={t.name} value={t.name}>{t.name} ({t.count})</option>
              ))}
            </select>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <div className="bg-slate-800 rounded-lg p-3">
              <p className="text-xs text-slate-400">Total RDV</p>
              <p className="text-2xl font-bold text-white">{filteredRdvBusinessDays.length}</p>
            </div>
            <div className="bg-slate-800 rounded-lg p-3">
              <p className="text-xs text-slate-400">Confirmés</p>
              <p className="text-2xl font-bold text-green-400">{statsByTelepro.reduce((a, b) => a + b.confirmed, 0)}</p>
            </div>
            <div className="bg-slate-800 rounded-lg p-3">
              <p className="text-xs text-slate-400">Taux confirmation</p>
              <p className="text-2xl font-bold text-blue-400">
                {filteredRdvBusinessDays.length > 0 ? Math.round(statsByTelepro.reduce((a, b) => a + b.confirmed, 0) / filteredRdvBusinessDays.length * 100) : 0}%
              </p>
            </div>
            <div className="bg-slate-800 rounded-lg p-3">
              <p className="text-xs text-slate-400">Moy./jour</p>
              <p className="text-2xl font-bold text-purple-400">
                {statsByDay.length > 0 ? Math.round(filteredRdvBusinessDays.length / statsByDay.length * 10) / 10 : 0}
              </p>
            </div>
          </div>
        </div>

        {/* Content based on view mode */}
        <div className="overflow-auto max-h-[55vh]">
          {viewMode === 'table' && (
            <table className="w-full">
              <thead className="bg-slate-900 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Client</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Dép.</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Télépro</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Date Prise RDV</th>
                  <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Confirmé</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Date R1</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {filteredRdv.map(client => (
                  <tr key={client.rowNumber} className="hover:bg-slate-700/50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-white font-medium">{client.nom} {client.prenom}</p>
                        <p className="text-xs text-slate-400">{client.logement} • {client.categorie}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="bg-slate-700 px-2 py-1 rounded text-sm text-white">{client.dpt}</span>
                    </td>
                    <td className="px-4 py-3 text-white text-sm">{client.telepro}</td>
                    <td className="px-4 py-3 text-white text-sm">{formatDateWithDay(client.datePriseRdv)}</td>
                    <td className="px-4 py-3 text-center">
                      {client.rdvConfirme?.toLowerCase() === 'oui' ? (
                        <span className="bg-green-600/20 text-green-400 px-2 py-1 rounded text-xs font-bold">OUI</span>
                      ) : client.rdvConfirme ? (
                        <span className="text-slate-400 text-sm">{client.rdvConfirme}</span>
                      ) : (
                        <span className="text-slate-600">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-white text-sm">{formatDateWithDay(client.dateR1) || <span className="text-slate-600">-</span>}</td>
                  </tr>
                ))}
                {filteredRdvBusinessDays.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                      Aucun RDV trouvé pour cette période
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {viewMode === 'chart' && (
            <div className="p-4 space-y-6">
              {/* Chart by day */}
              <div className="bg-slate-900 rounded-xl p-4">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-400" />
                  RDV par jour
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={statsByDay}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="label" stroke="#9CA3AF" fontSize={10} angle={-45} textAnchor="end" height={60} />
                      <YAxis stroke="#9CA3AF" fontSize={12} />
                      <RechartsTooltip 
                        contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                        labelStyle={{ color: '#F9FAFB' }}
                      />
                      <Legend />
                      <Bar dataKey="total" name="Total RDV" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="confirmed" name="Confirmés" fill="#10B981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Stats by Telepro */}
              <div className="bg-slate-900 rounded-xl p-4">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-400" />
                  Performance par Télépro
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={statsByTelepro} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis type="number" stroke="#9CA3AF" fontSize={12} />
                      <YAxis dataKey="name" type="category" stroke="#9CA3AF" fontSize={11} width={100} />
                      <RechartsTooltip 
                        contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                        labelStyle={{ color: '#F9FAFB' }}
                      />
                      <Legend />
                      <Bar dataKey="total" name="Total" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="confirmed" name="Confirmés" fill="#10B981" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {viewMode === 'compare' && (
            <div className="p-4 space-y-4">
              {/* Telepro selection */}
              <div className="bg-slate-900 rounded-xl p-4">
                <h3 className="text-sm font-bold text-white mb-3">Sélectionnez les télépros à comparer (max 5)</h3>
                <div className="flex flex-wrap gap-2">
                  {statsByTelepro.map((t, i) => (
                    <button
                      key={t.name}
                      onClick={() => toggleCompareSelection(t.name)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                        selectedForCompare.includes(t.name)
                          ? 'text-white'
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                      style={selectedForCompare.includes(t.name) ? { backgroundColor: COLORS[selectedForCompare.indexOf(t.name) % COLORS.length] } : {}}
                    >
                      {t.name}
                      <span className="text-xs opacity-75">({t.total})</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Comparison Chart */}
              {selectedForCompare.length >= 2 && (
                <div className="bg-slate-900 rounded-xl p-4">
                  <h3 className="text-lg font-bold text-white mb-4">Comparaison jour par jour</h3>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={comparisonData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="label" stroke="#9CA3AF" fontSize={10} angle={-45} textAnchor="end" height={60} />
                        <YAxis stroke="#9CA3AF" fontSize={12} />
                        <RechartsTooltip 
                          contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                          labelStyle={{ color: '#F9FAFB' }}
                        />
                        <Legend />
                        {selectedForCompare.map((name, i) => (
                          <Line
                            key={name}
                            type="monotone"
                            dataKey={name}
                            name={name}
                            stroke={COLORS[i % COLORS.length]}
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            connectNulls
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {selectedForCompare.length < 2 && (
                <div className="bg-slate-900 rounded-xl p-8 text-center text-slate-400">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Sélectionnez au moins 2 télépros pour comparer</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RdvConfirmesModal({ isOpen, onClose, telepros, allClients }) {
  const [selectedTelepro, setSelectedTelepro] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Initialiser les dates au mois courant
  useEffect(() => {
    if (isOpen) {
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setStartDate(firstDay.toISOString().split('T')[0]);
      setEndDate(lastDay.toISOString().split('T')[0]);
    }
  }, [isOpen]);

  const filteredRdv = useMemo(() => {
    if (!allClients) return [];
    
    return allClients.filter(client => {
      const datePriseRdv = client.datePriseRdv;
      // Filtrer uniquement les RDV confirmés
      if (!datePriseRdv || client.rdvConfirme?.toLowerCase() !== 'oui') return false;

      // Filtrer par télépro
      if (selectedTelepro && client.telepro !== selectedTelepro) return false;

      // Filtrer par date
      if (startDate || endDate) {
        const parts = datePriseRdv.split('/');
        if (parts.length === 3) {
          const rdvDate = new Date(parts[2], parts[1] - 1, parts[0]);
          if (startDate && rdvDate < new Date(startDate)) return false;
          if (endDate && rdvDate > new Date(endDate)) return false;
        }
      }

      return true;
    });
  }, [allClients, selectedTelepro, startDate, endDate]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-white">RDV Confirmés</h2>
              <p className="text-xs text-slate-400">{filteredRdv.length} rendez-vous confirmés</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="p-4 border-b border-slate-700 flex flex-wrap items-center gap-4">
          {/* Sélecteur Télépro */}
          <div>
            <label className="block text-xs text-slate-400 mb-1">Télépro</label>
            <select
              value={selectedTelepro}
              onChange={(e) => setSelectedTelepro(e.target.value)}
              className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500 min-w-[150px]"
            >
              <option value="">Tous</option>
              {telepros.map(t => (
                <option key={t.name} value={t.name}>{t.name}</option>
              ))}
            </select>
          </div>

          {/* Sélecteur de période */}
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartChange={setStartDate}
            onEndChange={setEndDate}
          />

          <button
            onClick={() => { setSelectedTelepro(''); setStartDate(''); setEndDate(''); }}
            className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg"
          >
            Réinitialiser
          </button>
        </div>

        <div className="overflow-auto max-h-[60vh]">
          <table className="w-full">
            <thead className="bg-slate-900 sticky top-0">
              <tr>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Client</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Dép.</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Télépro</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Date RDV</th>
                <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Confirmé</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Date R1</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {filteredRdv.map(client => (
                <tr key={client.rowNumber} className="hover:bg-slate-700/50">
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-white font-medium">{client.nom} {client.prenom}</p>
                      <p className="text-xs text-slate-400">{client.logement} • {client.categorie}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="bg-slate-700 px-2 py-1 rounded text-sm text-white">{client.dpt}</span>
                  </td>
                  <td className="px-4 py-3 text-white text-sm">{client.telepro}</td>
                  <td className="px-4 py-3 text-white text-sm">{client.datePriseRdv}</td>
                  <td className="px-4 py-3 text-center">
                    <span className="bg-green-600/20 text-green-400 px-2 py-1 rounded text-xs font-bold">OUI</span>
                  </td>
                  <td className="px-4 py-3 text-white text-sm">{client.dateR1 || <span className="text-slate-600">-</span>}</td>
                </tr>
              ))}
              {filteredRdv.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                    Aucun RDV confirmé trouvé pour cette période
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function DatesManquantesModal({ isOpen, onClose, allClients, onUpdate }) {
  const [editingRow, setEditingRow] = useState(null);
  const [dateInput, setDateInput] = useState('');
  const [saving, setSaving] = useState(false);

  const clientsSansDate = useMemo(() => {
    if (!allClients) return [];
    return allClients.filter(c => c.dateManquante);
  }, [allClients]);

  const handleSaveDate = async (client) => {
    if (!dateInput) return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/telepro/update-appel-qualite`, {
        row_number: client.rowNumber,
        nouveau_resultat: client.resultatAppel,
        date_manuelle: dateInput
      });
      setEditingRow(null);
      setDateInput('');
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error("Erreur:", err);
      alert("Erreur lors de la sauvegarde");
    }
    setSaving(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-600 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-white">Dates d'appel qualité manquantes</h2>
              <p className="text-xs text-slate-400">{clientsSansDate.length} client{clientsSansDate.length > 1 ? 's' : ''} à compléter</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="p-4 bg-yellow-600/10 border-b border-yellow-600/30">
          <p className="text-yellow-300 text-sm">
            ⚠️ Ces clients ont un résultat d'appel qualité mais pas de date. 
            Veuillez indiquer la date de l'appel pour chaque client.
          </p>
        </div>

        <div className="overflow-auto max-h-[60vh]">
          <table className="w-full">
            <thead className="bg-slate-900 sticky top-0">
              <tr>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Client</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Télépro</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Résultat CQ</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Date à ajouter</th>
                <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {clientsSansDate.map(client => (
                <tr key={client.rowNumber} className="hover:bg-slate-700/50">
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-white font-medium">{client.nom} {client.prenom}</p>
                      <p className="text-xs text-slate-400">Dép. {client.dpt}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-white text-sm">{client.telepro || '-'}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-1 rounded bg-slate-700 text-slate-300">
                      {client.resultatAppel}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {editingRow === client.rowNumber ? (
                      <input
                        type="date"
                        value={dateInput}
                        onChange={(e) => setDateInput(e.target.value)}
                        className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    ) : (
                      <span className="text-red-400 text-sm">Manquante</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {editingRow === client.rowNumber ? (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleSaveDate(client)}
                          disabled={saving || !dateInput}
                          className="px-3 py-1 bg-green-600 hover:bg-green-500 disabled:bg-slate-600 text-white text-xs rounded"
                        >
                          {saving ? '...' : 'Sauver'}
                        </button>
                        <button
                          onClick={() => { setEditingRow(null); setDateInput(''); }}
                          className="px-3 py-1 bg-slate-600 hover:bg-slate-500 text-white text-xs rounded"
                        >
                          Annuler
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setEditingRow(client.rowNumber); setDateInput(''); }}
                        className="px-3 py-1 bg-yellow-600 hover:bg-yellow-500 text-white text-xs rounded"
                      >
                        Ajouter date
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {clientsSansDate.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-green-400">
                    ✓ Toutes les dates sont complètes !
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function TeleproDashboard({ onBack, onClientClick, authUser, dashboardId, onMonEspaceClick, onQualificationAppelClick, onOpenClientFiche }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTelepro, setSelectedTelepro] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);
  const [search, setSearch] = useState('');
  const [showRdvModal, setShowRdvModal] = useState(false);
  const [showRdvConfirmesModal, setShowRdvConfirmesModal] = useState(false);
  const [showAppelQualiteModal, setShowAppelQualiteModal] = useState(false);
  const [showDatesManquantesModal, setShowDatesManquantesModal] = useState(false);
  const [showR1AVenirModal, setShowR1AVenirModal] = useState(false);
  const [ringoverStats, setRingoverStats] = useState(null);
  const [ringoverLoading, setRingoverLoading] = useState(true);
  
  // Pipeline stages filter
  const [pipelineStages, setPipelineStages] = useState([]);
  const [pipelineGroupes, setPipelineGroupes] = useState([]);
  const [selectedPipelineStage, setSelectedPipelineStage] = useState(null);
  const [pipelineLoading, setPipelineLoading] = useState(true);

  useEffect(() => {
    loadData();
    loadRingoverStats();
    loadPipelineConfig();
  }, []);
  
  // Charger la configuration du pipeline
  const loadPipelineConfig = async () => {
    setPipelineLoading(true);
    try {
      const [res, resGroupes] = await Promise.all([
        axios.get(`${API}/config/pipeline/etapes`),
        axios.get(`${API}/config/pipeline/groupes`)
      ]);
      // Extraire les étapes depuis res.data.etapes
      const allStages = res.data?.etapes || res.data || [];
      // Filtrer les étapes actives ET associées au dashboard
      const activeStages = allStages.filter(s => s.actif !== false && (s.dashboards || []).includes(dashboardId));
      setPipelineStages(activeStages);
      // Charger les groupes
      setPipelineGroupes(resGroupes.data?.groupes || []);
      // Sélectionner la première étape par défaut - ou client_temp si elle existe
      if (activeStages.length > 0 && !selectedPipelineStage) {
        const leadStage = activeStages.find(s => s.id === 'lead');
        setSelectedPipelineStage(leadStage ? 'lead' : activeStages[0].id);
      }
    } catch (err) {
      console.error("Error loading pipeline config:", err);
    }
    setPipelineLoading(false);
  };

  // Recharger les stats Ringover quand le télépro change
  useEffect(() => {
    loadRingoverStats(selectedTelepro);
  }, [selectedTelepro]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/telepro/dashboard`);
      setClients(res.data.clients || []);
    } catch (err) {
      console.error("Error loading telepro data:", err);
    }
    setLoading(false);
  };

  const loadRingoverStats = async (telepro = null) => {
    setRingoverLoading(true);
    try {
      const params = telepro ? { telepro } : {};
      const res = await axios.get(`${API}/ringover/stats`, { params });
      setRingoverStats(res.data);
    } catch (err) {
      console.error("Error loading Ringover stats:", err);
    }
    setRingoverLoading(false);
  };

  // Calculer les statistiques
  const stats = useMemo(() => {
    const telepros = {};
    const results = {};
    let r1AVenir = 0;
    let rdvConfirmes = 0;
    let qualiteAFaire = 0;
    let datesManquantes = 0;
    let aRelancer = 0;
    let bloques = 0;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    clients.forEach(c => {
      // Par télépro
      if (c.telepro) {
        telepros[c.telepro] = (telepros[c.telepro] || 0) + 1;
      }

      // Résultats appels
      if (c.resultatAppel) {
        results[c.resultatAppel] = (results[c.resultatAppel] || 0) + 1;
      }

      // R1 à venir (date R1 dans le futur et pas de résultat appel qualité)
      if (c.dateR1) {
        // Parser la date R1
        const parts = c.dateR1.split('/');
        if (parts.length === 3) {
          const dateR1 = new Date(parts[2], parts[1] - 1, parts[0]);
          if (dateR1 >= today && !c.resultatAppel) {
            r1AVenir++;
          }
        }
        if (c.rdvConfirme?.toLowerCase() === 'oui') {
          rdvConfirmes++;
        }
      }

      // Appels qualité à faire (a un R1 mais pas de résultat CQ)
      if (c.dateR1 && !c.resultatAppel) {
        qualiteAFaire++;
      }

      // Dates manquantes (résultat CQ présent mais pas de date)
      if (c.dateManquante) {
        datesManquantes++;
      }

      // À relancer (pas de RDV confirmé et date passée)
      if (c.datePriseRdv && c.rdvConfirme?.toLowerCase() !== 'oui') {
        aRelancer++;
      }

      // Bloqués
      const etat = c.etatsGeneraux?.toLowerCase() || '';
      if (etat.includes('bloqué') || etat.includes('annulé')) {
        bloques++;
      }
    });

    return {
      total: clients.length,
      r1AVenir,
      rdvConfirmes,
      qualiteAFaire,
      datesManquantes,
      aRelancer,
      bloques,
      telepros: Object.entries(telepros)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count),
      results: Object.entries(results)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
    };
  }, [clients]);

  // Liste des clients avec R1 à venir
  const clientsR1AVenir = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return clients
      .filter(c => {
        if (!c.dateR1 || c.resultatAppel) return false;
        const parts = c.dateR1.split('/');
        if (parts.length !== 3) return false;
        const dateR1 = new Date(parts[2], parts[1] - 1, parts[0]);
        return dateR1 >= today;
      })
      .sort((a, b) => {
        // Trier par date R1 croissante
        const partsA = a.dateR1.split('/');
        const partsB = b.dateR1.split('/');
        const dateA = new Date(partsA[2], partsA[1] - 1, partsA[0]);
        const dateB = new Date(partsB[2], partsB[1] - 1, partsB[0]);
        return dateA - dateB;
      });
  }, [clients]);

  // Filtrer les clients
  const filteredClients = useMemo(() => {
    return clients.filter(c => {
      // Filtre par étape du pipeline (principal + secondaires)
      if (selectedPipelineStage) {
        const allStages = c.pipelineStages || [c.pipelineStage || 'lead'];
        if (!allStages.includes(selectedPipelineStage)) return false;
      }
      if (selectedTelepro && c.telepro !== selectedTelepro) return false;
      if (selectedResult && c.resultatAppel !== selectedResult) return false;
      if (search) {
        const s = search.toLowerCase();
        const nom = (c.nom || '').toLowerCase();
        const prenom = (c.prenom || '').toLowerCase();
        if (!nom.includes(s) && !prenom.includes(s)) return false;
      }
      return true;
    });
  }, [clients, selectedPipelineStage, selectedTelepro, selectedResult, search]);

  // Calculer les compteurs par étape du pipeline (principal + secondaires)
  const pipelineCounts = useMemo(() => {
    const counts = {};
    clients.forEach(c => {
      const allStages = c.pipelineStages || [c.pipelineStage || 'lead'];
      allStages.forEach(stage => {
        counts[stage] = (counts[stage] || 0) + 1;
      });
    });
    return counts;
  }, [clients]);

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <header className="bg-slate-950 border-b border-slate-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-slate-800 rounded-lg">
              <X className="w-5 h-5 text-slate-400" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-lg">Télépro</h1>
                <p className="text-xs text-slate-400">Dashboard</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Badge notification taches */}
            <TaskNotificationBadge userEmail={authUser?.email} onOpenTasks={() => { if (typeof onMonEspaceClick === 'function') onMonEspaceClick('taches_internes'); }} />
            {/* Bouton Mon Espace - ouvre la page Mon Espace */}
            <button
              onClick={onMonEspaceClick}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors"
              data-testid="mon-espace-page-btn"
            >
              <User className="w-4 h-4" />
              Mon Espace
            </button>
            <CalendrierButton authUser={authUser} />
            {/* Bouton Profil avec Avatar (compact) */}
            <MonEspaceButton user={authUser} onLogout={onBack} compact={true} />
            <button 
              onClick={loadData}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
          </div>
        </div>
      </header>

      {/* Bannière SAN-IA sticky - reste visible en haut */}
      <div className="sticky top-0 z-40 px-6 pt-4 pb-2 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800/50">
        <SanIABanner 
          ringoverStats={ringoverStats}
          dashboardData={{ clients }}
          telepros={stats.telepros}
          allClients={clients}
          authUser={authUser}
        />
      </div>

      <div className="p-6 pt-4">
        {/* Alerte Dates Manquantes */}
        {stats.datesManquantes > 0 && (
          <div 
            onClick={() => setShowDatesManquantesModal(true)}
            className="mb-6 bg-yellow-600/20 border border-yellow-600 rounded-xl p-4 cursor-pointer hover:bg-yellow-600/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-yellow-500" />
              <div>
                <p className="font-bold text-yellow-400">
                  {stats.datesManquantes} appel{stats.datesManquantes > 1 ? 's' : ''} qualité sans date !
                </p>
                <p className="text-sm text-yellow-300/70">
                  Cliquez ici pour compléter les dates manquantes
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Carousel Alertes & Efficacité - Regroupe toutes les bannières */}
        <AlertesCarousel 
          clients={clients}
          selectedTelepro={selectedTelepro}
          onClientUpdate={loadData}
          authUser={authUser}
          telepros={stats.telepros}
          CollaboratorEfficiencyComponent={<CollaboratorEfficiency defaultExpanded={true} />}
          R1EfficiencyComponent={<R1Efficiency allClients={clients} telepros={stats.telepros} defaultExpanded={true} />}
          AppelsVsR1EfficiencyComponent={<AppelsVsR1Efficiency allClients={clients} defaultExpanded={true} />}
          onQualificationAppelClick={onQualificationAppelClick}
        />

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Filters */}
          <div className="space-y-4">
            {/* Mes Rappels en premier */}
            <MesRappelsWidget 
              authUser={authUser}
              onAppelerLead={(lead) => {
                console.log('Appeler rappel:', lead);
              }}
            />
            <CommunicationInterneWidget authUser={authUser} />
            <VisaIAWidget authUser={authUser} />
            <MaTodoListWidget authUser={authUser} />
            <TeleprosFilter 
              telepros={stats.telepros} 
              selected={selectedTelepro}
              onSelect={setSelectedTelepro}
            />
            <RingoverStatsCard 
              stats={ringoverStats}
              loading={ringoverLoading}
              selectedTelepro={selectedTelepro}
            />
          </div>

          {/* Client List */}
          <div className="lg:col-span-3 bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
            {/* Search and filters */}
            <div className="p-4 border-b border-slate-700 flex items-center gap-4">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 pl-10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <Eye className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              </div>
              {selectedTelepro && (
                <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm flex items-center gap-2">
                  {selectedTelepro}
                  <button onClick={() => setSelectedTelepro(null)}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {(selectedTelepro || selectedResult || search) && (
                <button
                  onClick={() => { setSelectedTelepro(null); setSelectedResult(null); setSearch(''); }}
                  className="text-sm text-slate-400 hover:text-white"
                >
                  × Réinitialiser
                </button>
              )}
            </div>

            {/* Pipeline Stages Filter */}
            <div className="px-4 py-3 border-b border-slate-700 bg-slate-800/50">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-slate-400 uppercase tracking-wider">Pipeline</span>
                {selectedPipelineStage && (
                  <button
                    onClick={() => setSelectedPipelineStage(null)}
                    className="text-xs text-slate-500 hover:text-slate-300"
                  >
                    (voir tous)
                  </button>
                )}
              </div>
              {pipelineLoading ? (
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Chargement...
                </div>
              ) : (
                <PipelineGroupedPills
                  stages={pipelineStages}
                  groups={pipelineGroupes}
                  counts={pipelineCounts}
                  selectedStage={selectedPipelineStage}
                  onSelectStage={setSelectedPipelineStage}
                  horizontal={true}
                />
              )}
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Nom</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Prénom</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Dépt</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Source</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Date réception</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Télépro</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Technicien</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Statut Pipeline</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Date R1</th>
                    <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {filteredClients.slice(0, 50).map(client => {
                    const stage = pipelineStages.find(s => s.id === (client.pipelineStage || 'lead'));
                    return (
                    <tr 
                      key={client.rowNumber} 
                      className="hover:bg-slate-700/50 cursor-pointer"
                      onClick={() => onClientClick(client.rowNumber, client.clientId)}
                    >
                      <td className="px-4 py-3 text-white font-medium text-sm">{client.nom || <span className="text-slate-600">-</span>}</td>
                      <td className="px-4 py-3 text-white text-sm">{client.prenom || <span className="text-slate-600">-</span>}</td>
                      <td className="px-4 py-3">
                        <span className="bg-slate-700 px-2 py-1 rounded text-sm text-white">{client.dpt}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-300 text-sm">{client.source || <span className="text-slate-600">-</span>}</td>
                      <td className="px-4 py-3 text-slate-300 text-sm">
                        {client.dateReception ? new Date(client.dateReception).toLocaleDateString('fr-FR') : <span className="text-slate-600">-</span>}
                      </td>
                      <td className="px-4 py-3 text-white text-sm">{client.telepro || <span className="text-slate-600">-</span>}</td>
                      <td className="px-4 py-3 text-white text-sm">{client.technicien || <span className="text-slate-600">-</span>}</td>
                      <td className="px-4 py-3">
                        {stage ? (
                          <span className="text-xs px-2 py-1 rounded font-medium" style={{ backgroundColor: `${stage.color}30`, color: stage.color }}>
                            {stage.nom}
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded bg-slate-700 text-slate-300">{client.pipelineStage || 'lead'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-white text-sm">{client.dateR1 || <span className="text-slate-600">-</span>}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={(e) => { e.stopPropagation(); onClientClick(client.rowNumber, client.clientId); }}
                          className="p-2 hover:bg-slate-600 rounded-lg"
                          data-testid={`view-client-${client.rowNumber}`}
                        >
                          <Eye className="w-4 h-4 text-slate-400" />
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredClients.length > 50 && (
              <div className="p-4 text-center text-slate-400 text-sm border-t border-slate-700">
                Affichage des 50 premiers résultats sur {filteredClients.length}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* RDV Pris Modal */}
      <RdvPrisModal
        isOpen={showRdvModal}
        onClose={() => setShowRdvModal(false)}
        telepros={stats.telepros}
        allClients={clients}
      />

      {/* RDV Confirmés Modal */}
      <RdvConfirmesModal
        isOpen={showRdvConfirmesModal}
        onClose={() => setShowRdvConfirmesModal(false)}
        telepros={stats.telepros}
        allClients={clients}
      />

      {/* Dates Manquantes Modal */}
      <DatesManquantesModal
        isOpen={showDatesManquantesModal}
        onClose={() => setShowDatesManquantesModal(false)}
        allClients={clients}
        onUpdate={loadData}
      />

      {/* Modal R1 à venir */}
      {showR1AVenirModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-2xl w-full max-w-5xl max-h-[85vh] overflow-hidden border border-slate-700 shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calendar className="w-6 h-6 text-white" />
                <h2 className="text-xl font-bold text-white">R1 à venir</h2>
                <span className="bg-white/20 px-3 py-1 rounded-full text-sm text-white">
                  {clientsR1AVenir.length} client{clientsR1AVenir.length > 1 ? 's' : ''}
                </span>
              </div>
              <button
                onClick={() => setShowR1AVenirModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-80px)]">
              {clientsR1AVenir.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Calendar className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Aucun R1 à venir</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left px-4 py-3 text-slate-400 text-sm font-medium">Date R1</th>
                      <th className="text-left px-4 py-3 text-slate-400 text-sm font-medium">Client</th>
                      <th className="text-left px-4 py-3 text-slate-400 text-sm font-medium">Télépro</th>
                      <th className="text-left px-4 py-3 text-slate-400 text-sm font-medium">Technicien</th>
                      <th className="text-left px-4 py-3 text-slate-400 text-sm font-medium">Source</th>
                      <th className="text-left px-4 py-3 text-slate-400 text-sm font-medium">Dpt</th>
                      <th className="text-left px-4 py-3 text-slate-400 text-sm font-medium">Téléphone</th>
                      <th className="text-left px-4 py-3 text-slate-400 text-sm font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientsR1AVenir.map((client, idx) => {
                      // Calculer si c'est aujourd'hui ou demain
                      const parts = client.dateR1.split('/');
                      const dateR1 = new Date(parts[2], parts[1] - 1, parts[0]);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const tomorrow = new Date(today);
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      
                      const isToday = dateR1.getTime() === today.getTime();
                      const isTomorrow = dateR1.getTime() === tomorrow.getTime();
                      
                      return (
                        <tr 
                          key={client.id || idx} 
                          className={`border-b border-slate-800 hover:bg-slate-800/50 cursor-pointer transition-colors ${
                            isToday ? 'bg-green-500/10' : isTomorrow ? 'bg-yellow-500/10' : ''
                          }`}
                          onClick={() => {
                            setShowR1AVenirModal(false);
                            if (onClientClick && client.rowNumber) {
                              onClientClick(client.rowNumber, client.clientId);
                            }
                          }}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className={`font-medium ${isToday ? 'text-green-400' : isTomorrow ? 'text-yellow-400' : 'text-white'}`}>
                                {client.dateR1}
                              </span>
                              {isToday && <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">Aujourd'hui</span>}
                              {isTomorrow && <span className="text-xs bg-yellow-500 text-black px-2 py-0.5 rounded-full">Demain</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="text-white font-medium">{client.nom}</p>
                              <p className="text-slate-400 text-sm">{client.prenom}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-blue-400">{client.telepro || '-'}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-purple-400">{client.technicien || '-'}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-slate-300">{client.source || client.categorie || '-'}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-slate-300">{client.dpt || '-'}</span>
                          </td>
                          <td className="px-4 py-3">
                            {client.telephone ? (
                              <a 
                                href={`tel:${client.telephone}`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
                              >
                                <Phone className="w-4 h-4" />
                                {client.telephone}
                              </a>
                            ) : (
                              <span className="text-slate-600">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowR1AVenirModal(false);
                                if (onClientClick && client.rowNumber) {
                                  onClientClick(client.rowNumber);
                                }
                              }}
                              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded-lg transition-colors"
                            >
                              Voir fiche
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Chat SAN-IA Télépro */}
      <SanIAChat
        poleId="telepro"
        poleName="Télépros"
        directorName="SAN-IA Télépro"
        directorAvatar="🎯"
        userName="Collaborateur"
      />

      {/* Widget Alertes Automatiques de Fatima */}
      <FatimaAlertsWidget currentUser={authUser} autoCheck={true} />

      {/* Bannière Leads Live en bas */}
      <BanniereLeadsLive 
        authUser={authUser}
        onOpenClientFiche={onOpenClientFiche}
        onAppelerLead={(lead) => {
          console.log('Appeler lead:', lead);
        }}
      />
    </div>
  );
}

export default TeleproDashboard;
